import { h, Component } from 'preact';
import { MiddlewareAuthService } from '../../../../services/telegram/MiddlewareAuthService';
import { TelegramAdapter } from '../../../../services/telegram/TelegramAdapter';
import Config from '../../config';
import { route } from 'preact-router';
import PageDataFeed from '../../../../services/page-data-feed';

interface State {
    status: 'IDLE' | 'LOADING_SDK' | 'AUTHENTICATING' | 'ASK_PHONE' | 'WAITING_WEBHOOK' | 'ERROR';
    errorMessage?: string;
}

export class TelegramOrchestrator extends Component<{} ,State> {
    private telegram: TelegramAdapter = TelegramAdapter.getInstance();
    private pollingInterval: number | null = null;
    private readonly middlewareService = new MiddlewareAuthService(Config.softionAPI);
    state: State = { 
        status: 'IDLE'
    };

    async componentDidMount() {
        await this.startFlow();
    }

    componentWillUnmount() {
        this.telegram.hideMainButton();
        if (this.pollingInterval) clearInterval(this.pollingInterval);
    }

    private async startFlow() {
        try {
            // 1. Caricamento SDK
            this.setState({ status: 'LOADING_SDK' });
            await this.telegram.initialize();

            // 2. Tentativo Login
            await this.attemptLogin();

        } catch (e) {
            console.error(e);
            this.setState({ status: 'ERROR', errorMessage: 'Impossible to load Telegram Environment' });
        }
    }

    async onLoginSuccess() {
        await PageDataFeed.userLoginAction();
        route('/', true)
    }

    private async attemptLogin() {
        this.setState({ status: 'AUTHENTICATING' });
        this.telegram.hideMainButton(); // Pulisce bottoni precedenti

        const initData = this.telegram.getRawInitData();
        const result = await this.middlewareService.authenticate(initData);

        if (result.success) {
            // SUCCESSO: Notifica il parent e redirigi
            this.onLoginSuccess();
            // Non serve setState, verremo smontati dal router
        } else if (result.error === 'ask-phone') {
            // CASO 403: Serve il telefono
            this.handleAskPhone();
        } else {
            // ERRORE GENERICO
            this.telegram.showAlert("Authentication failed: " + result.error);
            this.setState({ status: 'ERROR', errorMessage: result.error });
        }
    }

    private handleAskPhone() {
        this.setState({ status: 'ASK_PHONE' });
        
        // Configuriamo il "Main Button" nativo di Telegram
        // È molto meglio UX rispetto a un bottone HTML
        this.telegram.setupMainButton("SHARE PHONE NUMBER", async () => {
            // Al click, chiediamo il contatto
            const shared = await this.telegram.requestPhoneContact();
            if (shared) {
                // Se l'utente ha condiviso, Telegram ha mandato il webhook a Java.
                // Passiamo in attesa.
                this.waitForWebhookSync();
            }
        });
    }

    private waitForWebhookSync() {
        this.setState({ status: 'WAITING_WEBHOOK' });
        this.telegram.hideMainButton(); // Nascondi il bottone per evitare doppi click
        
        // Iniziamo un polling aggressivo (ogni 2s) verso il middleware Java
        // Finché non ci risponde 200 (trovato nel DB) o timeout.
        let attempts = 0;
        this.pollingInterval = window.setInterval(async () => {
            attempts++;
            if (attempts > 15) { // Timeout dopo 30s
                if (this.pollingInterval) clearInterval(this.pollingInterval);
                this.telegram.showAlert("Timeout awaiting server confirmation. Please try again.");
                this.handleAskPhone(); // Torna indietro
                return;
            }

            // Riprova il login silenziosamente
            const initData = this.telegram.getRawInitData();
            const result = await this.middlewareService.authenticate(initData);

            if (result.success) {
                if (this.pollingInterval) 
                    clearInterval(this.pollingInterval);
                this.onLoginSuccess();
            }
            // Se ancora 403 ask-phone, continua ad aspettare il webhook
        }, 2000);
    }

    render() {
        const { status, errorMessage } = this.state;

        // UI Di fallback mentre l'orchestrator lavora.
        // Nota: Telegram mostra il suo loader finché non chiamiamo .ready(),
        // ma noi lo chiamiamo subito dopo il load dello script.
        
        return (
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100vh',
                color: 'var(--tg-theme-text-color, #000)',
                backgroundColor: 'var(--tg-theme-bg-color, #fff)' 
            }}>
                {status === 'LOADING_SDK' && <p>Connecting to Telegram...</p>}
                
                {status === 'AUTHENTICATING' && <div class="spinner">Logging in...</div>}
                
                {status === 'ASK_PHONE' && (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <h3>Almost there!</h3>
                        <p>We need your phone number to sync your account.</p>
                        <p>Please click the button below.</p>
                        {/* Il bottone vero è quello nativo in basso, qui mettiamo solo istruzioni */}
                    </div>
                )}

                {status === 'WAITING_WEBHOOK' && (
                    <div style={{ textAlign: 'center' }}>
                        <div class="spinner"></div>
                        <p>Syncing contact info...</p>
                    </div>
                )}

                {status === 'ERROR' && (
                    <div style={{ color: 'red' }}>
                        <h3>Error</h3>
                        <p>{errorMessage}</p>
                        <button onClick={() => this.attemptLogin()}>Retry</button>
                    </div>
                )}
            </div>
        );
    }
}
